import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { adminApi, laveriesApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatPrice, formatDate, timeAgo, paginate } from '../utils/helpers';

// ─── Constantes ───────────────────────────────────────────────────────────────
const SERVICE_TYPES = [
  { value: 'lavage',           label: 'Lavage' },
  { value: 'pressing',         label: 'Pressing' },
  { value: 'repassage',        label: 'Repassage' },
  { value: 'lavage_repassage', label: 'Lavage + Repassage' },
];

const TYPE_COLORS = {
  lavage:           'badge-blue',
  pressing:         'badge-purple',
  repassage:        'badge-orange',
  lavage_repassage: 'badge-green',
};

const TYPE_ICONS = {
  lavage:           '💧',
  pressing:         '👔',
  repassage:        '♨️',
  lavage_repassage: '✨',
};

const DEFAULT_SERVICE_FORM = {
  type: 'lavage',
  unit: 'kilo',
  item_name: '',
  price: '',
  estimated_duration: '24',
  is_active: true,
};

// ─── Sous-composant : card d'un service ──────────────────────────────────────
const ServiceCard = ({ service, onEdit, onToggle, onDelete }) => {
  const typeLabel  = SERVICE_TYPES.find(t => t.value === service.type)?.label || service.type;
  const typeColor  = TYPE_COLORS[service.type] || 'badge-gray';
  const typeIcon   = TYPE_ICONS[service.type]  || '🔧';

  return (
    <div style={{
      background: 'var(--bg3)',
      border: `1px solid ${service.is_active ? 'var(--border)' : 'var(--border)'}`,
      borderRadius: 12,
      padding: '16px 18px',
      opacity: service.is_active ? 1 : 0.55,
      transition: 'all 0.2s',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* barre de couleur gauche */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: service.is_active ? 'var(--accent)' : 'var(--border2)',
        borderRadius: '3px 0 0 3px',
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 18 }}>{typeIcon}</span>
            <span style={{ fontWeight: 700, fontSize: 14, fontFamily: 'Syne' }}>
              {service.item_name || typeLabel}
            </span>
            <Badge className={typeColor}>{typeLabel}</Badge>
            {!service.is_active && <Badge className="badge-red">Inactif</Badge>}
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text2)', flexWrap: 'wrap' }}>
            <span>⚖️ Au {service.unit === 'kilo' ? 'kilogramme' : 'pièce'}</span>
            <span>⏱️ ~{service.estimated_duration}h</span>
            <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 15 }}>
              {formatPrice(service.price)}{service.unit === 'kilo' ? '/kg' : '/pcs'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => onEdit(service)} title="Modifier">✎</button>
          <button
            className={`btn btn-sm ${service.is_active ? 'btn-danger' : 'btn-success'}`}
            onClick={() => onToggle(service)}
            title={service.is_active ? 'Désactiver' : 'Activer'}
          >
            {service.is_active ? '⏸' : '▶'}
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => onDelete(service)} title="Supprimer">🗑</button>
        </div>
      </div>
    </div>
  );
};

// ─── Sous-composant : Panneau de prix système ────────────────────────────────
const PricingPanel = () => {
  const toast = useToast();

  // Config système existante (commission, livraison, points)
  const { data: raw,    loading,   error,    refetch   } = useApi(() => adminApi.getPricing());
  // Config pricing globale v2.4 (seuil kg, remise, textes d'aide)
  const { data: cfgRaw, loading: cfgLoading, error: cfgError, refetch: cfgRefetch } =
    useApi(() => adminApi.getPricingConfig());

  const [form,     setForm]    = useState(null);
  const [cfgForm,  setCfgForm] = useState(null);
  const [saving,   setSaving]  = useState(false);
  const [cfgSaving,setCfgSaving]= useState(false);
  const [edited,   setEdited]  = useState(false);
  const [cfgEdited,setCfgEdited]= useState(false);

  useEffect(() => {
    if (raw?.data && !edited)    setForm({ ...raw.data });
  }, [raw, edited]);

  useEffect(() => {
    if (cfgRaw?.data && !cfgEdited) setCfgForm({ ...cfgRaw.data });
  }, [cfgRaw, cfgEdited]);

  const handleChange = (key, val) => { setForm(p => ({ ...p, [key]: val })); setEdited(true); };
  const handleCfgChange = (key, val) => { setCfgForm(p => ({ ...p, [key]: val })); setCfgEdited(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.setPricing({
        commission_rate:         Number(form.commission_rate),
        delivery_fee_base:       Number(form.delivery_fee_base),
        delivery_fee_per_km:     Number(form.delivery_fee_per_km),
        points_per_mad:          Number(form.points_per_mad),
        points_to_discount_rate: Number(form.points_to_discount_rate),
      });
      toast.success('Configuration des prix système mise à jour ✓');
      setEdited(false); refetch();
    } catch (err) { toast.error(err.message || 'Erreur'); } finally { setSaving(false); }
  };

  const handleCfgSave = async () => {
    setCfgSaving(true);
    try {
      await adminApi.setPricingConfig({
        default_kg_threshold: Number(cfgForm.default_kg_threshold),
        auto_discount_kg:     Number(cfgForm.auto_discount_kg),
        help_text_piece:      cfgForm.help_text_piece || '',
        help_text_kg:         cfgForm.help_text_kg    || '',
      });
      toast.success('Configuration pricing globale mise à jour ✓ — Impact immédiat sur l\'application');
      setCfgEdited(false); cfgRefetch();
    } catch (err) { toast.error(err.message || 'Erreur'); } finally { setCfgSaving(false); }
  };

  if (loading || cfgLoading) return <Loading text="Chargement des prix système..." />;
  if (error)                  return <ApiError message={error} onRetry={refetch} />;
  if (!form) return null;

  const fields = [
    { key: 'commission_rate',        label: 'Taux de commission',           hint: 'Part LAVEX sur chaque commande',          icon: '🏦', type: 'percent', accent: 'var(--accent)'  },
    { key: 'delivery_fee_base',      label: 'Frais de livraison de base',   hint: 'Montant fixe par commande (MAD)',          icon: '🛵', type: 'mad',     accent: 'var(--blue)'    },
    { key: 'delivery_fee_per_km',    label: 'Prix par kilomètre',           hint: 'Tarif additionnel au km (MAD/km)',         icon: '📍', type: 'mad_km',  accent: 'var(--purple)'  },
    { key: 'points_per_mad',         label: 'Points par MAD dépensé',       hint: '0.1 = 1 MAD → 0.1 point de fidélité',    icon: '⭐', type: 'ratio',   accent: 'var(--orange)'  },
    { key: 'points_to_discount_rate',label: 'Valeur d\'un point en MAD',    hint: '0.1 = 10 points → 1 MAD de réduction',   icon: '🎁', type: 'ratio',   accent: 'var(--green)'   },
  ];

  const displayVal = (f) => {
    const v = form[f.key];
    if (f.type === 'percent') return `${(v * 100).toFixed(0)}%`;
    if (f.type === 'mad')     return `${v} MAD`;
    if (f.type === 'mad_km')  return `${v} MAD/km`;
    return v;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Section 1 : Commission / Livraison / Points ── */}
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">💰 Prix système & Commissions</div>
            <div className="card-subtitle">Impact immédiat sur toutes les nouvelles commandes</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {edited && <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ ...raw.data }); setEdited(false); }}>↺ Annuler</button>}
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !edited}>
              {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '💾 Sauvegarder'}
            </button>
          </div>
        </div>

        {edited && (
          <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
            <span>⚠️</span><span>Modifications non sauvegardées — cliquez sur "Sauvegarder" pour appliquer.</span>
          </div>
        )}

        <div className="grid-2" style={{ gap: 16 }}>
          {fields.map(f => (
            <div key={f.key} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: f.accent }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: 18 }}>{f.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{f.hint}</div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Syne', color: f.accent }}>{displayVal(f)}</div>
              </div>
              <input type="number" className="form-input" step={f.type === 'percent' || f.type === 'ratio' ? '0.01' : '0.5'} min="0" value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)} style={{ marginBottom: 0 }} />
              {f.type === 'percent' && <div className="form-hint" style={{ marginTop: 6 }}>Saisir en décimal : 0.15 = 15%</div>}
            </div>
          ))}
        </div>

        {/* Simulation */}
        <div style={{ marginTop: 20, background: 'var(--bg3)', borderRadius: 10, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
            📊 Simulation — commande de 100 MAD sur 3 km
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {[
              { l: 'Sous-total',   v: '100.00 MAD' },
              { l: 'Livraison',    v: `${(Number(form.delivery_fee_base) + Number(form.delivery_fee_per_km) * 3).toFixed(2)} MAD` },
              { l: 'Commission',   v: `${(100 * Number(form.commission_rate)).toFixed(2)} MAD` },
              { l: 'Total client', v: `${(100 + Number(form.delivery_fee_base) + Number(form.delivery_fee_per_km) * 3).toFixed(2)} MAD` },
              { l: 'Points gagnés',v: `${(100 * Number(form.points_per_mad)).toFixed(1)} pts` },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>{item.l}</div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{item.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Section 2 : Config pricing globale v2.4 ── */}
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">⚖️ Configuration Pricing kg / pièce</div>
            <div className="card-subtitle">Seuil de déverrouillage kg, remise automatique et textes d'aide affichés dans l'application</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {cfgEdited && <button className="btn btn-ghost btn-sm" onClick={() => { setCfgForm({ ...cfgRaw.data }); setCfgEdited(false); }}>↺ Annuler</button>}
            <button className="btn btn-primary btn-sm" onClick={handleCfgSave} disabled={cfgSaving || !cfgEdited}>
              {cfgSaving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '💾 Sauvegarder'}
            </button>
          </div>
        </div>

        {cfgError && <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 16 }}><span>⚠️</span><span>Config pricing non disponible (endpoint à créer côté backend)</span></div>}

        {cfgEdited && (
          <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
            <span>⚠️</span><span>Modifications non sauvegardées</span>
          </div>
        )}

        {cfgForm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="form-row">
              {/* Seuil kg */}
              <div className="form-group">
                <label className="form-label">
                  ⚖️ Seuil de déverrouillage kg (pièces)
                </label>
                <input
                  type="number" min="1" step="1"
                  className="form-input"
                  value={cfgForm.default_kg_threshold ?? 5}
                  onChange={e => handleCfgChange('default_kg_threshold', e.target.value)}
                />
                <div className="form-hint">
                  Nombre minimum de pièces pour qu'un client puisse basculer en tarification au kg.
                  Valeur par défaut appliquée aux services en mode <code>both</code> si non défini individuellement.
                </div>
              </div>
              {/* Remise kg */}
              <div className="form-group">
                <label className="form-label">
                  🏷️ Remise automatique au kg (%)
                </label>
                <input
                  type="number" min="0" max="100" step="0.5"
                  className="form-input"
                  value={cfgForm.auto_discount_kg ?? 0}
                  onChange={e => handleCfgChange('auto_discount_kg', e.target.value)}
                />
                <div className="form-hint">
                  Remise affichée à l'utilisateur quand il déverrouille la tarification kg.
                  Ex : 25 = "Vous économisez ~25%".
                </div>
              </div>
            </div>

            {/* Textes d'aide */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">📝 Texte d'aide — À la pièce</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ex: Prix par pièce, idéal pour 1 à 4 articles"
                  value={cfgForm.help_text_piece ?? ''}
                  onChange={e => handleCfgChange('help_text_piece', e.target.value)}
                />
                <div className="form-hint">Affiché sous le toggle "À la pièce" dans l'app client.</div>
              </div>
              <div className="form-group">
                <label className="form-label">📝 Texte d'aide — Au kg</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ex: Disponible dès {threshold} pièces — plus économique"
                  value={cfgForm.help_text_kg ?? ''}
                  onChange={e => handleCfgChange('help_text_kg', e.target.value)}
                />
                <div className="form-hint">
                  Utilisez <code>{'{threshold}'}</code> pour insérer dynamiquement le seuil du service.
                </div>
              </div>
            </div>

            {/* Prévisualisation texte kg */}
            {cfgForm.help_text_kg && (
              <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Prévisualisation (seuil = {cfgForm.default_kg_threshold ?? 5})
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                  {(cfgForm.help_text_kg || '').replace('{threshold}', cfgForm.default_kg_threshold ?? 5)}
                </div>
              </div>
            )}

            {/* ── Toggle kg_orders_enabled (v3.0) ── */}
            <div className="divider" />
            <div style={{ background: !cfgForm.kg_orders_enabled ? 'rgba(220,38,38,0.04)' : 'rgba(22,163,74,0.04)', borderRadius: 12, padding: '16px 18px', border: `1px solid ${!cfgForm.kg_orders_enabled ? 'rgba(220,38,38,0.15)' : 'rgba(22,163,74,0.15)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>
                    ⚖️ Autoriser la tarification au kg
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                    {cfgForm.kg_orders_enabled
                      ? 'Activé — les clients peuvent passer des commandes au kg'
                      : 'Désactivé (v3.0) — seule la tarification à la pièce est disponible'}
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: cfgForm.kg_orders_enabled ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                    {cfgForm.kg_orders_enabled ? 'ON' : 'OFF'}
                  </span>
                  <input
                    type="checkbox"
                    checked={!!cfgForm.kg_orders_enabled}
                    onChange={e => handleCfgChange('kg_orders_enabled', e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: 'var(--green)', cursor: 'pointer' }}
                  />
                </label>
              </div>
              {cfgForm.kg_orders_enabled && (
                <div className="inline-alert inline-alert-yellow" style={{ marginTop: 12 }}>
                  <span>⚠️</span>
                  <div style={{ fontSize: 12 }}>
                    En activant le kg, les clients verront à nouveau le toggle pièce/kg dans l'application.
                    Assurez-vous que les prix au kg sont configurés pour tous les services concernés.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Panneau : Gestion des prix par service (v2.4 / v3.0) ───────────────────
// ⚠️ v3.0 : mode kg retiré de l'UI (kg_orders_enabled = false côté backend)
// Seul le mode piece_only est utilisable.
const PRICING_MODES = [
  { value: 'piece_only', label: 'À la pièce', icon: '👔', color: 'var(--blue)' },
  // kg_only et both désactivés tant que kg_orders_enabled = false (v3.0)
];

const DEFAULT_SERVICE_PRICING = {
  price_per_piece: '',
  price_per_kg:    '',
  kg_threshold:    5,
  pricing_mode:    'piece_only', // v3.0 : kg désactivé par défaut
  default_unit:    'piece',
  reason:          '',
};

// Sous-composant : Modale historique des prix
const PricingHistoryModal = ({ serviceId, serviceName, onClose }) => {
  const { data: raw, loading, error } = useApi(
    () => serviceId ? adminApi.getServicePricingHistory(serviceId, { limit: 50 }) : Promise.resolve({ data: [] }),
    [serviceId]
  );
  const history = raw?.data || [];

  return (
    <Modal
      open={!!serviceId}
      onClose={onClose}
      title={`Historique des prix — ${serviceName}`}
      size="md"
      footer={<button className="btn btn-secondary" onClick={onClose}>Fermer</button>}
    >
      {loading ? <Loading text="Chargement..." />
        : error ? <ApiError message={error} />
        : history.length === 0
          ? <EmptyState icon="📋" title="Aucun historique" text="Aucune modification de prix enregistrée pour ce service." />
          : (
            <div style={{ position: 'relative', paddingLeft: 20 }}>
              <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />
              {history.map((entry, i) => (
                <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 16, position: 'relative' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, marginTop: 3, background: 'var(--accent)', zIndex: 1 }} />
                  <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                        {entry.admin_name || 'Admin'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDate(entry.created_at)}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                      {entry.old_price_per_piece !== undefined && (
                        <div style={{ background: 'var(--bg2)', borderRadius: 6, padding: '6px 10px' }}>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Prix/pièce</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: 'var(--red)', textDecoration: 'line-through', fontSize: 11 }}>
                              {entry.old_price_per_piece != null ? `${entry.old_price_per_piece} MAD` : '—'}
                            </span>
                            <span style={{ color: 'var(--text3)' }}>→</span>
                            <span style={{ color: 'var(--green)', fontWeight: 700 }}>
                              {entry.new_price_per_piece != null ? `${entry.new_price_per_piece} MAD` : '—'}
                            </span>
                          </div>
                        </div>
                      )}
                      {entry.old_price_per_kg !== undefined && (
                        <div style={{ background: 'var(--bg2)', borderRadius: 6, padding: '6px 10px' }}>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Prix/kg</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: 'var(--red)', textDecoration: 'line-through', fontSize: 11 }}>
                              {entry.old_price_per_kg != null ? `${entry.old_price_per_kg} MAD` : '—'}
                            </span>
                            <span style={{ color: 'var(--text3)' }}>→</span>
                            <span style={{ color: 'var(--green)', fontWeight: 700 }}>
                              {entry.new_price_per_kg != null ? `${entry.new_price_per_kg} MAD` : '—'}
                            </span>
                          </div>
                        </div>
                      )}
                      {entry.old_kg_threshold !== undefined && (
                        <div style={{ background: 'var(--bg2)', borderRadius: 6, padding: '6px 10px' }}>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Seuil kg</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: 'var(--red)', textDecoration: 'line-through', fontSize: 11 }}>{entry.old_kg_threshold ?? '—'}</span>
                            <span style={{ color: 'var(--text3)' }}>→</span>
                            <span style={{ color: 'var(--green)', fontWeight: 700 }}>{entry.new_kg_threshold ?? '—'}</span>
                          </div>
                        </div>
                      )}
                      {entry.old_pricing_mode !== undefined && (
                        <div style={{ background: 'var(--bg2)', borderRadius: 6, padding: '6px 10px' }}>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Mode</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: 'var(--red)', textDecoration: 'line-through', fontSize: 11 }}>{entry.old_pricing_mode ?? '—'}</span>
                            <span style={{ color: 'var(--text3)' }}>→</span>
                            <span style={{ color: 'var(--green)', fontWeight: 700 }}>{entry.new_pricing_mode ?? '—'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {entry.reason && (
                      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text2)', fontStyle: 'italic' }}>
                        "{entry.reason}"
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
      }
    </Modal>
  );
};

// Composant principal du panneau
const ServicesPricingPanel = () => {
  const toast = useToast();

  // Filtres
  const [laverieFilter,  setLaverieFilter]  = useState('');
  const [typeFilter,     setTypeFilter]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search,         setSearch]         = useState('');
  const [page,           setPage]           = useState(1);

  // État édition inline
  const [editRows,  setEditRows]  = useState({});   // { service_id: { ...form } }
  const [saving,    setSaving]    = useState({});    // { service_id: bool }
  const [histModal, setHistModal] = useState(null);  // { id, name }

  const { data: raw, loading, error, refetch, setData } = useApi(
    () => adminApi.getServicesPricing({
      laverie_id:   laverieFilter  || undefined,
      type:         typeFilter     || undefined,
      category_id:  categoryFilter || undefined,
    }),
    [laverieFilter, typeFilter, categoryFilter]
  );

  // Catégories de services pour le filtre
  const { data: catRaw } = useApi(() => adminApi.getServiceCategories());

  const services = raw?.data || [];

  // Laveries distinctes pour filtre
  const laverieOptions = useMemo(() => {
    const seen = new Set();
    return services.filter(s => {
      if (seen.has(s.laverie_id)) return false;
      seen.add(s.laverie_id); return true;
    }).map(s => ({ id: s.laverie_id, name: s.laverie_name }));
  }, [services]);

  // Filtre local + recherche
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return services.filter(s =>
      !q || s.laverie_name?.toLowerCase().includes(q) || s.item_name?.toLowerCase().includes(q) || s.type?.toLowerCase().includes(q)
    );
  }, [services, search]);

  const paged = paginate(filtered, page, 15);

  // Récupérer la forme d'édition d'un service (initialisée depuis les données ou depuis editRows)
  const getEditForm = (s) => editRows[s.service_id] ?? {
    price_per_piece: s.price_per_piece ?? '',
    price_per_kg:    s.price_per_kg    ?? '',
    kg_threshold:    s.kg_threshold    ?? 5,
    pricing_mode:    s.pricing_mode    || 'both',
    default_unit:    s.default_unit    || 'piece',
    reason:          '',
  };

  const setEditField = (serviceId, key, val) => {
    setEditRows(prev => ({
      ...prev,
      [serviceId]: { ...(prev[serviceId] ?? getEditForm(services.find(s => s.service_id === serviceId) ?? {})), [key]: val },
    }));
  };

  // Validation
  const validate = (form) => {
    const mode = form.pricing_mode;
    if (mode !== 'kg_only'    && (form.price_per_piece === '' || isNaN(Number(form.price_per_piece)))) return 'Prix/pièce requis pour ce mode.';
    if (mode !== 'piece_only' && (form.price_per_kg    === '' || isNaN(Number(form.price_per_kg))))    return 'Prix/kg requis pour ce mode.';
    if (mode === 'both'       && Number(form.kg_threshold) < 1) return 'Seuil kg doit être ≥ 1.';
    return null;
  };

  // Sauvegarder un service
  const handleSave = async (serviceId) => {
    const form = editRows[serviceId];
    if (!form) return;
    const err = validate(form);
    if (err) { toast.error(err); return; }

    setSaving(prev => ({ ...prev, [serviceId]: true }));
    try {
      await adminApi.updateServicePricing(serviceId, {
        price_per_piece: form.pricing_mode !== 'kg_only'    ? Number(form.price_per_piece) : null,
        price_per_kg:    form.pricing_mode !== 'piece_only' ? Number(form.price_per_kg)    : null,
        kg_threshold:    Number(form.kg_threshold),
        pricing_mode:    form.pricing_mode,
        default_unit:    form.default_unit,
        reason:          form.reason || 'Mise à jour admin',
      });
      toast.success('Prix mis à jour ✓');
      // Mettre à jour localement
      setData(prev => ({
        ...prev,
        data: prev.data.map(s => s.service_id === serviceId ? { ...s, ...form } : s),
      }));
      setEditRows(prev => { const n = { ...prev }; delete n[serviceId]; return n; });
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const isEdited    = (id) => !!editRows[id];
  const isSaving    = (id) => !!saving[id];

  // Statut de complétude
  const getStatus = (s, form) => {
    const mode = form.pricing_mode;
    const needPiece = mode !== 'kg_only';
    const needKg    = mode !== 'piece_only';
    const okPiece = !needPiece || (form.price_per_piece !== '' && form.price_per_piece != null && !isNaN(Number(form.price_per_piece)));
    const okKg    = !needKg    || (form.price_per_kg    !== '' && form.price_per_kg    != null && !isNaN(Number(form.price_per_kg)));
    return okPiece && okKg;
  };

  return (
    <div>
      {/* Filtres */}
      <div className="filter-bar" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Laverie, service, article..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>

        {laverieOptions.length > 1 && (
          <select className="filter-select" value={laverieFilter} onChange={e => { setLaverieFilter(e.target.value); setPage(1); }}>
            <option value="">Toutes les laveries</option>
            {laverieOptions.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}

        <select className="filter-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">Tous les types</option>
          {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {/* Filtre catégorie (v3.0) */}
        {catRaw?.data?.length > 0 && (
          <select className="filter-select" value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
            <option value="">Toutes les catégories</option>
            {catRaw.data.map(c => <option key={c.category_id} value={c.category_id}>{c.icon} {c.name}</option>)}
          </select>
        )}

        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {filtered.filter(s => {
            const f = getEditForm(s);
            return !getStatus(s, f);
          }).length} service(s) incomplet(s) / {filtered.length}
        </span>
      </div>

      {/* Alerte services incomplets */}
      {services.filter(s => !getStatus(s, getEditForm(s))).length > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 16 }}>
          <span>⚠️</span>
          <div style={{ fontSize: 12 }}>
            <strong>{services.filter(s => !getStatus(s, getEditForm(s))).length} service(s)</strong> n'ont pas tous leurs prix configurés. Ils ne seront pas proposés correctement dans l'application client.
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading text="Chargement des services..." />
          : error ? <ApiError message={error} onRetry={refetch} />
          : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Laverie / Service</th>
                      <th>Mode</th>
                      <th style={{ color: 'var(--blue)' }}>Prix / pièce</th>
                      <th>Statut</th>
                      <th style={{ minWidth: 220 }}>Raison</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.items.map(s => {
                      const form     = getEditForm(s);
                      const edited   = isEdited(s.service_id);
                      const saving_  = isSaving(s.service_id);
                      const ok       = getStatus(s, form);
                      const mode     = PRICING_MODES.find(m => m.value === form.pricing_mode);
                      const typeIcon = TYPE_ICONS[s.type] || '🔧';
                      const pieceDisabled = form.pricing_mode === 'kg_only';

                      return (
                        <tr key={s.service_id} style={{ background: edited ? 'rgba(91,106,240,0.03)' : undefined }}>
                          {/* Laverie / Service */}
                          <td style={{ minWidth: 160 }}>
                            <div className="td-main">{typeIcon} {s.item_name || SERVICE_TYPES.find(t => t.value === s.type)?.label}</div>
                            <div className="td-sub">{s.laverie_name}</div>
                          </td>

                          {/* Mode — v3.0 : piece_only uniquement */}
                          <td style={{ minWidth: 120 }}>
                            <Badge className="badge-blue">{PRICING_MODES[0].icon} {PRICING_MODES[0].label}</Badge>
                          </td>

                          {/* Prix / pièce */}
                          <td style={{ minWidth: 110 }}>
                            <input
                              type="number" min="0" step="0.5"
                              className="form-input"
                              style={{ marginBottom: 0, fontSize: 13, padding: '5px 8px' }}
                              value={form.price_per_piece}
                              onChange={e => setEditField(s.service_id, 'price_per_piece', e.target.value)}
                              placeholder="0.00"
                            />
                            {form.price_per_piece !== '' && (
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>MAD/pcs</div>
                            )}
                          </td>

                          {/* Statut */}
                          <td>
                            <Badge className={ok ? 'badge-green' : 'badge-red'}>
                              {ok ? '✅ Complet' : '❌ Incomplet'}
                            </Badge>
                          </td>

                          {/* Raison */}
                          <td style={{ minWidth: 200 }}>
                            <input
                              type="text"
                              className="form-input"
                              style={{ marginBottom: 0, fontSize: 12, padding: '5px 8px', opacity: edited ? 1 : 0.4 }}
                              placeholder={edited ? 'Raison du changement...' : '—'}
                              disabled={!edited}
                              value={form.reason || ''}
                              onChange={e => setEditField(s.service_id, 'reason', e.target.value)}
                            />
                          </td>

                          {/* Actions */}
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: 5 }}>
                              {edited && (
                                <>
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleSave(s.service_id)}
                                    disabled={saving_}
                                  >
                                    {saving_ ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '💾'}
                                  </button>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setEditRows(prev => { const n = { ...prev }; delete n[s.service_id]; return n; })}
                                    disabled={saving_}
                                  >
                                    ✕
                                  </button>
                                </>
                              )}
                              <button
                                className="btn btn-ghost btn-sm"
                                title="Historique des prix"
                                onClick={() => setHistModal({ id: s.service_id, name: s.item_name || s.type })}
                              >
                                📋
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {paged.items.length === 0 && !loading && (
                  <EmptyState icon="💰" title="Aucun service" text="Aucun service ne correspond aux filtres sélectionnés." />
                )}
              </div>

              {paged.total > 0 && (
                <div style={{ padding: '0 16px 16px' }}>
                  <Pagination {...paged} onPage={setPage} />
                </div>
              )}
            </>
          )
        }
      </div>

      {/* Modale historique */}
      {histModal && (
        <PricingHistoryModal
          serviceId={histModal.id}
          serviceName={histModal.name}
          onClose={() => setHistModal(null)}
        />
      )}
    </div>
  );
};

// ─── Panneau : Demandes de services des laveries (v2.2.2) ────────────────────
const STATUS_REQ = {
  pending:  { label: 'En attente', cls: 'badge-yellow', icon: '⏳' },
  approved: { label: 'Approuvée',  cls: 'badge-green',  icon: '✅' },
  rejected: { label: 'Rejetée',    cls: 'badge-red',    icon: '❌' },
};

const ServiceRequestsPanel = ({ onProcessed }) => {
  const toast = useToast();

  const [statusFilter, setStatusFilter] = useState('pending');
  const [laverieFilter, setLaverieFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Modale approbation
  const [approveModal, setApproveModal] = useState(null);
  const [finalPrice,   setFinalPrice]   = useState('');
  const [finalDuration,setFinalDuration]= useState('');
  const [approving,    setApproving]    = useState(false);

  // Modale rejet
  const [rejectModal,  setRejectModal]  = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting,    setRejecting]    = useState(false);

  const { data: raw, loading, error, refetch, setData } = useApi(
    () => adminApi.getServiceRequests({ status: statusFilter }),
    [statusFilter]
  );

  const requests = raw?.data || [];

  // Filtre côté client sur nom laverie ou nom service
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return requests.filter(r => {
      const matchSearch = !q
        || r.laverie_name?.toLowerCase().includes(q)
        || r.item_name?.toLowerCase().includes(q)
        || r.type?.toLowerCase().includes(q);
      const matchLav = !laverieFilter || String(r.laverie_id) === laverieFilter;
      return matchSearch && matchLav;
    });
  }, [requests, search, laverieFilter]);

  const paged = paginate(filtered, page, 12);

  // Laveries distinctes pour le filtre
  const laverieOptions = useMemo(() => {
    const seen = new Set();
    return requests.filter(r => {
      if (seen.has(r.laverie_id)) return false;
      seen.add(r.laverie_id);
      return true;
    }).map(r => ({ id: r.laverie_id, name: r.laverie_name }));
  }, [requests]);

  // ── Ouvrir modale approbation ─────────────────────────────────────────────
  const openApprove = (req) => {
    setApproveModal(req);
    setFinalPrice(String(req.proposed_price));
    setFinalDuration(String(req.estimated_duration || 24));
  };

  // ── Approuver ─────────────────────────────────────────────────────────────
  const handleApprove = async () => {
    setApproving(true);
    try {
      const body = {};
      const parsed = parseFloat(finalPrice);
      if (!isNaN(parsed)) body.final_price = parsed;
      const dur = parseInt(finalDuration);
      if (!isNaN(dur))   body.estimated_duration = dur;

      const res = await adminApi.approveServiceRequest(approveModal.request_id, body);
      const { final_price, service_id } = res.data || {};
      toast.success(
        `✅ Demande approuvée — Service #${service_id} créé pour "${approveModal.laverie_name}" à ${final_price ?? finalPrice} MAD`
      );
      // Retirer de la liste si on affiche seulement "pending"
      if (statusFilter === 'pending') {
        setData(prev => ({ ...prev, data: prev.data.filter(r => r.request_id !== approveModal.request_id) }));
      } else {
        setData(prev => ({
          ...prev,
          data: prev.data.map(r =>
            r.request_id === approveModal.request_id ? { ...r, status: 'approved' } : r
          ),
        }));
      }
      setApproveModal(null);
      if (onProcessed) onProcessed();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'approbation');
    } finally {
      setApproving(false);
    }
  };

  // ── Rejeter ───────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('La raison du rejet est obligatoire.'); return; }
    setRejecting(true);
    try {
      await adminApi.rejectServiceRequest(rejectModal.request_id, { rejection_reason: rejectReason.trim() });
      toast.success(`Demande rejetée — Notification envoyée à "${rejectModal.laverie_name}"`);
      if (statusFilter === 'pending') {
        setData(prev => ({ ...prev, data: prev.data.filter(r => r.request_id !== rejectModal.request_id) }));
      } else {
        setData(prev => ({
          ...prev,
          data: prev.data.map(r =>
            r.request_id === rejectModal.request_id ? { ...r, status: 'rejected', rejection_reason: rejectReason.trim() } : r
          ),
        }));
      }
      setRejectModal(null);
      setRejectReason('');
      if (onProcessed) onProcessed();
    } catch (err) {
      toast.error(err.message || 'Erreur lors du rejet');
    } finally {
      setRejecting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const pendingCount = statusFilter === 'pending' ? requests.length : null;

  return (
    <div>
      {/* Alerte si demandes en attente */}
      {pendingCount > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
          <span>📬</span>
          <div>
            <strong>{pendingCount} demande(s) de service</strong> en attente de traitement —
            Des laveries ont proposé de nouveaux services. Approuvez ou rejetez chaque demande.
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder="Laverie, service, article..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {/* Filtre statut */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 3 }}>
          {Object.entries(STATUS_REQ).map(([k, v]) => (
            <button
              key={k}
              className={`btn btn-sm ${statusFilter === k ? 'btn-primary' : 'btn-ghost'}`}
              style={{ borderRadius: 8 }}
              onClick={() => { setStatusFilter(k); setPage(1); }}
            >
              {v.icon} {v.label}
              {k === 'pending' && requests.length > 0 && statusFilter !== 'pending' && (
                <span style={{ marginLeft: 5, background: 'var(--red)', color: '#fff', borderRadius: 8, padding: '1px 6px', fontSize: 10 }}>
                  {requests.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Filtre laverie */}
        {laverieOptions.length > 1 && (
          <select className="filter-select" value={laverieFilter} onChange={e => { setLaverieFilter(e.target.value); setPage(1); }}>
            <option value="">Toutes les laveries</option>
            {laverieOptions.map(l => (
              <option key={l.id} value={String(l.id)}>{l.name}</option>
            ))}
          </select>
        )}

        <button className="btn btn-ghost btn-sm" onClick={refetch} title="Rafraîchir">↺</button>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
          {filtered.length} demande{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading text="Chargement des demandes..." />
          : error ? <ApiError message={error} onRetry={refetch} />
          : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Laverie</th>
                    <th>Service demandé</th>
                    <th>Prix proposé</th>
                    <th>Durée</th>
                    <th>Description</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.items.map(r => {
                    const sc  = STATUS_REQ[r.status] || { label: r.status, cls: 'badge-gray', icon: '⚪' };
                    const typeLabel = SERVICE_TYPES.find(t => t.value === r.type)?.label || r.type;
                    const typeIcon  = TYPE_ICONS[r.type] || '🔧';
                    const typeColor = TYPE_COLORS[r.type] || 'badge-gray';
                    return (
                      <tr key={r.request_id}>
                        {/* Laverie */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar name={r.laverie_name} size="sm" />
                            <div>
                              <div className="td-main">{r.laverie_name}</div>
                              <Badge className={r.laverie_category === 'A' ? 'badge-green' : 'badge-blue'} style={{ fontSize: 10 }}>
                                Cat. {r.laverie_category}
                              </Badge>
                            </div>
                          </div>
                        </td>

                        {/* Service */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <span>{typeIcon}</span>
                            <span className="td-main">{r.item_name || typeLabel}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <Badge className={typeColor}>{typeLabel}</Badge>
                            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                              {r.unit === 'kilo' ? '⚖️ /kg' : '👔 /pcs'}
                            </span>
                          </div>
                        </td>

                        {/* Prix proposé */}
                        <td>
                          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>
                            {formatPrice(r.proposed_price)}
                          </span>
                          <div className="td-sub">{r.unit === 'kilo' ? 'par kg' : 'par pièce'}</div>
                        </td>

                        {/* Durée */}
                        <td style={{ color: 'var(--text2)', fontSize: 13 }}>
                          ⏱️ {r.estimated_duration}h
                        </td>

                        {/* Description */}
                        <td style={{ maxWidth: 200 }}>
                          {r.description
                            ? <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
                                {r.description.length > 80 ? r.description.slice(0, 80) + '…' : r.description}
                              </span>
                            : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                          }
                        </td>

                        {/* Date */}
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          <div style={{ color: 'var(--text)' }}>{formatDate(r.created_at)}</div>
                          <div style={{ color: 'var(--text3)' }}>{timeAgo(r.created_at)}</div>
                          {r.processed_at && (
                            <div style={{ color: 'var(--text3)', fontSize: 11 }}>
                              Traité {timeAgo(r.processed_at)}
                            </div>
                          )}
                        </td>

                        {/* Statut */}
                        <td>
                          <div>
                            <Badge className={sc.cls}>{sc.icon} {sc.label}</Badge>
                            {r.rejection_reason && (
                              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4, maxWidth: 140 }}>
                                {r.rejection_reason.length > 60
                                  ? r.rejection_reason.slice(0, 60) + '…'
                                  : r.rejection_reason}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td onClick={e => e.stopPropagation()}>
                          {r.status === 'pending' ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => openApprove(r)}
                              >
                                ✓ Approuver
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => { setRejectModal(r); setRejectReason(''); }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                              {r.processed_by_name ? `Par ${r.processed_by_name}` : '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {paged.items.length === 0 && !loading && (
                <EmptyState
                  icon="📬"
                  title={statusFilter === 'pending' ? 'Aucune demande en attente' : 'Aucune demande'}
                  text={statusFilter === 'pending'
                    ? 'Toutes les demandes ont été traitées.'
                    : 'Aucune demande dans cette catégorie.'}
                />
              )}
            </div>

            {paged.total > 0 && (
              <div style={{ padding: '0 16px 16px' }}>
                <Pagination {...paged} onPage={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODALE APPROBATION ── */}
      <Modal
        open={!!approveModal}
        onClose={() => setApproveModal(null)}
        title={`Approuver — ${approveModal?.item_name || approveModal?.type}`}
        size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setApproveModal(null)} disabled={approving}>
              Annuler
            </button>
            <button className="btn btn-success" onClick={handleApprove} disabled={approving}>
              {approving
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Approbation...</>
                : '✓ Confirmer et créer le service'}
            </button>
          </>
        }
      >
        {approveModal && (
          <div>
            {/* Récap demande */}
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                {TYPE_ICONS[approveModal.type]} {approveModal.item_name || SERVICE_TYPES.find(t => t.value === approveModal.type)?.label}
              </div>
              {[
                { l: 'Laverie',  v: `${approveModal.laverie_name} (Cat. ${approveModal.laverie_category})` },
                { l: 'Type',     v: SERVICE_TYPES.find(t => t.value === approveModal.type)?.label },
                { l: 'Unité',    v: approveModal.unit === 'kilo' ? 'Au kilogramme' : 'À la pièce' },
              ].map((row, i) => (
                <div key={i} className="info-row" style={{ padding: '4px 0' }}>
                  <span className="info-label">{row.l}</span>
                  <span className="info-value">{row.v}</span>
                </div>
              ))}
              {approveModal.description && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  "{approveModal.description}"
                </div>
              )}
            </div>

            {/* Ajustement prix */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Prix final (MAD/{approveModal.unit === 'kilo' ? 'kg' : 'pcs'})
                </label>
                <input
                  type="number"
                  className="form-input"
                  min="0"
                  step="0.5"
                  value={finalPrice}
                  onChange={e => setFinalPrice(e.target.value)}
                />
                {parseFloat(finalPrice) !== approveModal.proposed_price && (
                  <div className="form-hint" style={{ color: 'var(--orange)' }}>
                    Prix proposé : {formatPrice(approveModal.proposed_price)} — vous l'avez modifié
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Durée estimée (heures)</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  value={finalDuration}
                  onChange={e => setFinalDuration(e.target.value)}
                />
              </div>
            </div>

            <div className="inline-alert inline-alert-green">
              <span>✅</span>
              <div style={{ fontSize: 12 }}>
                Le service sera <strong>créé automatiquement</strong> dans la liste de la laverie.
                Une notification sera envoyée à <strong>{approveModal.laverie_name}</strong>.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── MODALE REJET ── */}
      <Modal
        open={!!rejectModal}
        onClose={() => { setRejectModal(null); setRejectReason(''); }}
        title={`Rejeter — ${rejectModal?.item_name || rejectModal?.type}`}
        size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setRejectModal(null); setRejectReason(''); }} disabled={rejecting}>
              Annuler
            </button>
            <button className="btn btn-danger" onClick={handleReject} disabled={rejecting || !rejectReason.trim()}>
              {rejecting
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Rejet...</>
                : '✕ Rejeter la demande'}
            </button>
          </>
        }
      >
        {rejectModal && (
          <div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                {TYPE_ICONS[rejectModal.type]} {rejectModal.item_name || rejectModal.type}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>
                {rejectModal.laverie_name} · {formatPrice(rejectModal.proposed_price)}/{rejectModal.unit === 'kilo' ? 'kg' : 'pcs'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Raison du rejet *</label>
              <textarea
                className="form-textarea"
                rows={4}
                placeholder="Ex: Prix proposé trop bas (min 25 MAD), service déjà couvert par lavage standard..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
              <div className="form-hint">Cette raison sera transmise à la laverie par notification.</div>
            </div>

            <div className="inline-alert inline-alert-yellow">
              <span>📣</span>
              <div style={{ fontSize: 12 }}>
                La laverie recevra une notification avec la raison du refus et pourra soumettre une nouvelle demande.
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export const Services = () => {
  const toast = useToast();
  const location = useLocation();

  // Liste des laveries (admin endpoint) pour le sélecteur
  const { data: lavRaw, loading: lavLoading, error: lavError, refetch: lavRefetch } =
    useApi(() => adminApi.getLaveriesList());

  // Badge dynamique : nombre de demandes en attente
  const { data: pendingReqRaw, refetch: refetchPending } =
    useApi(() => adminApi.getServiceRequests({ status: 'pending' }));
  const pendingReqCount = pendingReqRaw?.data?.length || 0;

  // Laverie sélectionnée
  const [selectedLav, setSelectedLav] = useState(null);

  // Auto-sélectionner si on arrive depuis la page Laveries avec un state
  // ou auto-ouvrir l'onglet demandes si location.state.tab = 'requests'
  useEffect(() => {
    if (location.state?.tab === 'requests') {
      setTab('requests');
    }
    if (lavRaw?.data && location.state?.laverie_id && !selectedLav) {
      const lav = lavRaw.data.find(l => l.laverie_id === location.state.laverie_id);
      if (lav) handleSelectLav(lav);
    }
  // eslint-disable-next-line
  }, [lavRaw]);

  // Détail de la laverie sélectionnée (inclut les services)
  const [lavDetail,     setLavDetail]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState(null);

  // Filtres services
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search,       setSearch]       = useState('');

  // Modals
  const [addModal,    setAddModal]    = useState(false);
  const [editModal,   setEditModal]   = useState(null);   // service en cours d'édition
  const [deleteModal, setDeleteModal] = useState(null);   // service à supprimer

  // Formulaire service
  const [form,   setForm]   = useState(DEFAULT_SERVICE_FORM);
  const [saving, setSaving] = useState(false);

  // Tab actif : 'services' | 'pricing' | 'requests'
  const [tab, setTab] = useState(location.state?.tab === 'requests' ? 'requests' : 'services');

  const laveries = lavRaw?.data || [];

  // Charger le détail d'une laverie (services inclus)
  const loadDetail = useCallback(async (lav) => {
    if (!lav) { setLavDetail(null); return; }
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await laveriesApi.get(lav.laverie_id);
      setLavDetail(res.data);
    } catch (err) {
      setDetailError(err.message || 'Impossible de charger les services de cette laverie');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleSelectLav = useCallback((lav) => {
    setSelectedLav(lav);
    setTypeFilter('all');
    setStatusFilter('all');
    setSearch('');
    loadDetail(lav);
  }, [loadDetail]);

  // Services filtrés
  const services = lavDetail?.services || [];
  const filteredServices = useMemo(() => {
    const q = search.toLowerCase();
    return services.filter(s => {
      const matchSearch = !q
        || s.item_name?.toLowerCase().includes(q)
        || s.type?.toLowerCase().includes(q);
      const matchType   = typeFilter   === 'all' || s.type    === typeFilter;
      const matchStatus = statusFilter === 'all'
        || (statusFilter === 'active' ? s.is_active : !s.is_active);
      return matchSearch && matchType && matchStatus;
    });
  }, [services, search, typeFilter, statusFilter]);

  // ── Actions service ─────────────────────────────────────────────────────────
  const refreshDetail = useCallback(() => loadDetail(selectedLav), [loadDetail, selectedLav]);

  const handleAdd = async () => {
    if (!form.price) { toast.error('Le prix est obligatoire.'); return; }
    if (form.unit === 'piece' && !form.item_name.trim()) {
      toast.error('Le nom de l\'article est obligatoire pour le tarif à la pièce.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.addService(selectedLav.laverie_id, {
        type:               form.type,
        unit:               form.unit,
        item_name:          form.unit === 'piece' ? form.item_name.trim() : null,
        price:              Number(form.price),
        estimated_duration: Number(form.estimated_duration) || 24,
        is_active:          form.is_active,
      });
      toast.success('Service créé ✓');
      setAddModal(false);
      setForm(DEFAULT_SERVICE_FORM);
      await refreshDetail();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!form.price) { toast.error('Le prix est obligatoire.'); return; }
    setSaving(true);
    try {
      await adminApi.updateService(selectedLav.laverie_id, editModal.service_id, {
        type:               form.type,
        unit:               form.unit,
        item_name:          form.unit === 'piece' ? form.item_name.trim() : null,
        price:              Number(form.price),
        estimated_duration: Number(form.estimated_duration) || 24,
        is_active:          form.is_active,
      });
      toast.success('Service mis à jour ✓');
      setEditModal(null);
      await refreshDetail();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (service) => {
    try {
      await adminApi.updateService(selectedLav.laverie_id, service.service_id, {
        is_active: !service.is_active,
      });
      toast.success(service.is_active ? 'Service désactivé' : 'Service activé ✓');
      await refreshDetail();
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await adminApi.deleteService(selectedLav.laverie_id, deleteModal.service_id);
      toast.success('Service supprimé définitivement');
      setDeleteModal(null);
      await refreshDetail();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (service) => {
    setEditModal(service);
    setForm({
      type:               service.type,
      unit:               service.unit,
      item_name:          service.item_name || '',
      price:              service.price,
      estimated_duration: service.estimated_duration,
      is_active:          service.is_active,
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (lavLoading) return <Loading text="Chargement des laveries..." />;
  if (lavError)   return <ApiError message={lavError} onRetry={lavRefetch} />;

  return (
    <div>
      {/* TABS */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 4, width: 'fit-content', flexWrap: 'wrap' }}>
        {[
          { key: 'services',       label: '🔧 Services & Tarifs' },
          { key: 'service-pricing',label: '💰 Prix par service'  },
          { key: 'pricing',        label: '⚙️ Config système'   },
          { key: 'requests',       label: '📬 Demandes laveries', badge: pendingReqCount },
        ].map(t => (
          <button
            key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
            style={{ borderRadius: 9, position: 'relative', gap: 6 }}
          >
            {t.label}
            {t.badge > 0 && (
              <span style={{
                background: tab === t.key ? 'rgba(0,0,0,0.25)' : 'var(--red)',
                color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
              }}>
                {t.badge > 99 ? '99+' : t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB: SERVICES ── */}
      {tab === 'services' && (
        <div className="grid-layout-services">
          {/* Colonne gauche : sélecteur de laverie */}
          <div>
            <div className="card" style={{ padding: 0, position: 'sticky', top: 20 }}>
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
                <div className="card-title" style={{ fontSize: 13 }}>🏪 Choisir une laverie</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{laveries.length} laveries au total</div>
              </div>
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {laveries.length === 0 ? (
                  <EmptyState icon="🏪" title="Aucune laverie" text="Créez d'abord une laverie." />
                ) : (
                  laveries.map(lav => (
                    <button
                      key={lav.laverie_id}
                      onClick={() => handleSelectLav(lav)}
                      style={{
                        width: '100%', textAlign: 'left', padding: '12px 18px',
                        background: selectedLav?.laverie_id === lav.laverie_id ? 'rgba(232,240,74,0.07)' : 'none',
                        border: 'none', borderBottom: '1px solid var(--border)',
                        borderLeft: selectedLav?.laverie_id === lav.laverie_id ? '3px solid var(--accent)' : '3px solid transparent',
                        cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <Avatar name={lav.name} size="sm" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 600,
                          color: selectedLav?.laverie_id === lav.laverie_id ? 'var(--accent)' : 'var(--text)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {lav.name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 8, marginTop: 2 }}>
                          <Badge className={lav.category === 'A' ? 'badge-green' : 'badge-blue'} style={{ fontSize: 10 }}>Cat. {lav.category}</Badge>
                          <span>{lav.is_active !== false ? '● Actif' : '○ Inactif'}</span>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Colonne droite : services de la laverie sélectionnée */}
          <div>
            {!selectedLav ? (
              <div className="card">
                <EmptyState
                  icon="👈"
                  title="Sélectionnez une laverie"
                  text="Choisissez une laverie dans la liste pour gérer ses services et prix."
                />
              </div>
            ) : (
              <>
                {/* Header laverie */}
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                      <Avatar name={selectedLav.name} size="lg" />
                      <div>
                        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 17 }}>{selectedLav.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                          {selectedLav.address} · {selectedLav.phone}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <Badge className={selectedLav.category === 'A' ? 'badge-green' : 'badge-blue'}>
                            Catégorie {selectedLav.category}
                          </Badge>
                          <Badge className="badge-gray">
                            {services.length} service{services.length !== 1 ? 's' : ''}
                          </Badge>
                          <Badge className={services.filter(s => s.is_active).length > 0 ? 'badge-green' : 'badge-red'}>
                            {services.filter(s => s.is_active).length} actif{services.filter(s => s.is_active).length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => { setAddModal(true); setForm(DEFAULT_SERVICE_FORM); }}
                    >
                      + Ajouter un service
                    </button>
                  </div>
                </div>

                {/* Filtres */}
                <div className="filter-bar" style={{ marginBottom: 16 }}>
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                      className="search-input"
                      placeholder="Nom du service..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <select className="filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="all">Type: Tous</option>
                    {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                  <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">Statut: Tous</option>
                    <option value="active">Actifs</option>
                    <option value="inactive">Inactifs</option>
                  </select>
                  <button className="btn btn-ghost btn-sm" onClick={refreshDetail} title="Rafraîchir">↺</button>
                  <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    {filteredServices.length} / {services.length} services
                  </span>
                </div>

                {/* Contenu */}
                {detailLoading ? (
                  <Loading text="Chargement des services..." />
                ) : detailError ? (
                  <ApiError message={detailError} onRetry={refreshDetail} />
                ) : filteredServices.length === 0 ? (
                  <div className="card">
                    <EmptyState
                      icon="🔧"
                      title={services.length === 0 ? "Aucun service configuré" : "Aucun service correspondant"}
                      text={services.length === 0
                        ? "Ajoutez le premier service pour cette laverie."
                        : "Modifiez les filtres."}
                      action={services.length === 0 && (
                        <button className="btn btn-primary" onClick={() => setAddModal(true)}>
                          + Ajouter un service
                        </button>
                      )}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {filteredServices.map(service => (
                      <ServiceCard
                        key={service.service_id}
                        service={service}
                        onEdit={openEdit}
                        onToggle={handleToggle}
                        onDelete={setDeleteModal}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: PRICING ── */}
      {tab === 'pricing'        && <PricingPanel />}
      {tab === 'service-pricing' && <ServicesPricingPanel />}
      {tab === 'requests'       && <ServiceRequestsPanel onProcessed={refetchPending} />}

      {/* ── MODAL: AJOUTER SERVICE ── */}
      <ServiceFormModal
        open={addModal}
        onClose={() => setAddModal(false)}
        title={`Ajouter un service — ${selectedLav?.name}`}
        form={form}
        setForm={setForm}
        onSubmit={handleAdd}
        saving={saving}
        submitLabel="Créer le service"
      />

      {/* ── MODAL: MODIFIER SERVICE ── */}
      <ServiceFormModal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title={`Modifier — ${editModal?.item_name || editModal?.type}`}
        form={form}
        setForm={setForm}
        onSubmit={handleEdit}
        saving={saving}
        submitLabel="Enregistrer"
      />

      {/* ── MODAL: CONFIRMER SUPPRESSION ── */}
      <ConfirmModal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={handleDelete}
        title="Supprimer le service"
        message={`Supprimer "${deleteModal?.item_name || deleteModal?.type}" ? Cette action est définitive et ne peut pas être annulée.`}
        confirmLabel="Supprimer définitivement"
        danger
      />
    </div>
  );
};

// ─── Formulaire service (partagé add/edit) ────────────────────────────────────
const ServiceFormModal = ({ open, onClose, title, form, setForm, onSubmit, saving, submitLabel }) => {
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={onSubmit} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : submitLabel}
          </button>
        </>
      }
    >
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Type de service *</label>
          <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
            {SERVICE_TYPES.map(t => (
              <option key={t.value} value={t.value}>{TYPE_ICONS[t.value]} {t.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Unité de facturation *</label>
          <select className="form-select" value={form.unit} onChange={e => set('unit', e.target.value)}>
            <option value="kilo">⚖️ Au kilogramme</option>
            <option value="piece">👔 À la pièce</option>
          </select>
        </div>
      </div>

      {form.unit === 'piece' && (
        <div className="form-group">
          <label className="form-label">Nom de l'article *</label>
          <input
            className="form-input"
            placeholder="ex: Chemise, Pantalon, Veste..."
            value={form.item_name}
            onChange={e => set('item_name', e.target.value)}
          />
          <div className="form-hint">Requis pour la tarification à la pièce</div>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">
            Prix *{' '}
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>
              ({form.unit === 'kilo' ? 'MAD/kg' : 'MAD/pcs'})
            </span>
          </label>
          <input
            className="form-input"
            type="number"
            min="0"
            step="0.5"
            placeholder="25.00"
            value={form.price}
            onChange={e => set('price', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Durée estimée (heures)</label>
          <input
            className="form-input"
            type="number"
            min="1"
            placeholder="24"
            value={form.estimated_duration}
            onChange={e => set('estimated_duration', e.target.value)}
          />
          <div className="form-hint">Temps de traitement approximatif</div>
        </div>
      </div>

      {/* Prévisualisation */}
      {form.price && (
        <div style={{
          background: 'rgba(232,240,74,0.06)',
          border: '1px solid rgba(232,240,74,0.2)',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          gap: 20,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>TYPE</div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {TYPE_ICONS[form.type]} {SERVICE_TYPES.find(t => t.value === form.type)?.label}
            </div>
          </div>
          {form.item_name && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>ARTICLE</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{form.item_name}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>PRIX</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--accent)' }}>
              {formatPrice(Number(form.price))}/{form.unit === 'kilo' ? 'kg' : 'pcs'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>DÉLAI</div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>~{form.estimated_duration}h</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          type="checkbox"
          id="service_active"
          checked={form.is_active}
          onChange={e => set('is_active', e.target.checked)}
          style={{ accentColor: 'var(--accent)', width: 16, height: 16, cursor: 'pointer' }}
        />
        <label htmlFor="service_active" style={{ fontSize: 13, cursor: 'pointer' }}>
          Service actif (visible aux clients lors de la commande)
        </label>
      </div>
    </Modal>
  );
};
